<template>
  <div class="shared-order-page">
    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner">⏳</div>
      <p>{{ $t('common.loading') }}</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="error-state">
      <div class="error-icon">❌</div>
      <p>{{ error }}</p>
    </div>

    <!-- Content -->
    <div v-else-if="order" class="order-content">
      <!-- Order Header -->
      <div class="order-header">
        <div class="order-status" :class="getStatusClass(order.status)">
          {{ getStatusText(order.status) }}
        </div>
        <div class="order-no">订单号: {{ order.orderNo }}</div>
      </div>

      <!-- Order Items -->
      <div class="order-section">
        <div class="section-title">{{ $t('share.SharedOrder.line27_text') }}</div>
        <div class="order-items">
          <div v-for="item in order.items" :key="item.productId" class="order-item">
            <img v-if="item.productImage" :src="getImageUrl(item.productImage)" class="item-image" />
            <div v-else class="item-image-placeholder">📦</div>
            <div class="item-info">
              <div class="item-name">{{ item.productName }}</div>
              <div class="item-spec" v-if="item.specification">{{ item.specification }}</div>
              <div class="item-spec">
                {{ item.quantity }} {{ item.unit }} × S$ {{ formatPrice(item.unitPrice) }}
              </div>
              <div class="item-tax" v-if="item.taxRate && item.taxRate > 0">
                GST {{ Math.round(item.taxRate * 100) }}%
              </div>
            </div>
            <div class="item-price">
              S$ {{ formatPrice(item.subtotal) }}
            </div>
          </div>
        </div>
        
        <!-- 金额汇总 -->
        <div class="order-summary">
          <div class="summary-row">
            <span>{{ $t('share.SharedOrder.line51_text') }}</span>
            <span>S$ {{ formatPrice(order.subtotal) }}</span>
          </div>
          <div class="summary-row" v-if="order.gstAmount > 0">
            <span>{{ $t('share.SharedOrder.line55_text') }}</span>
            <span>S$ {{ formatPrice(order.gstAmount) }}</span>
          </div>
          <div class="summary-row total">
            <span>{{ $t('share.SharedOrder.line59_text') }}</span>
            <span class="total-amount">S$ {{ formatPrice(order.totalAmount) }}</span>
          </div>
        </div>
      </div>

      <!-- Delivery Info -->
      <div class="order-section" v-if="order.deliveryInfo">
        <div class="section-title">{{ $t('share.SharedOrder.line67_text') }}</div>
        <div class="delivery-info">
          <div class="info-row" v-if="order.deliveryInfo.receiverName">
            <span class="info-label">{{ $t('share.SharedOrder.line70_text') }}</span>
            <span class="info-value">{{ order.deliveryInfo.receiverName }}</span>
          </div>
          <div class="info-row" v-if="order.deliveryInfo.receiverPhone">
            <span class="info-label">{{ $t('share.SharedOrder.line74_text') }}</span>
            <span class="info-value">{{ order.deliveryInfo.receiverPhone }}</span>
          </div>
          <div class="info-row" v-if="order.deliveryInfo.address">
            <span class="info-label">{{ $t('share.SharedOrder.line78_text') }}</span>
            <span class="info-value">{{ order.deliveryInfo.address }}</span>
          </div>
        </div>
      </div>

      <!-- Sharer Info -->
      <div class="order-section">
        <div class="section-title">{{ $t('share.SharedOrder.line86_text') }}</div>
        <div class="sharer-info">
          <div class="info-row">
            <span class="info-label">{{ $t('share.SharedOrder.line89_text') }}</span>
            <span class="info-value">{{ sharer.name || '批发商' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ $t('share.SharedOrder.line93_text') }}</span>
            <span class="info-value">{{ sharer.type === 'WHOLESALER' ? '批发商' : '档口' }}</span>
          </div>
        </div>
      </div>

      <!-- Order Time -->
      <div class="order-section">
        <div class="section-title">{{ $t('share.SharedOrder.line101_text') }}</div>
        <div class="order-info">
          <div class="info-row">
            <span class="info-label">{{ $t('share.SharedOrder.line104_text') }}</span>
            <span class="info-value">{{ formatDateTime(order.createdAt) }}</span>
          </div>
          <div class="info-row" v-if="shareInfo.expireAt">
            <span class="info-label">{{ $t('share.SharedOrder.line108_text') }}</span>
            <span class="info-value">{{ formatDateTime(shareInfo.expireAt) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Actions -->
    <div class="bottom-actions" v-if="order">
      <button class="btn-login" @click="goLogin">{{ $t('share.SharedOrder.line117_text') }}</button>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import { getImageUrl } from '@/utils/image';

export default {
  name: 'SharedOrder',
  data() {
    return {
      loading: true,
      error: null,
      order: null,
      sharer: {},
      shareInfo: {}
    }
  },
  created() {
    this.loadSharedOrder()
  },
  methods: {
    getImageUrl,
    async loadSharedOrder() {
      const shareCode = this.$route.params.shareCode
      if (!shareCode) {
        this.error = '分享链接无效'
        this.loading = false
        return
      }

      try {
        const res = await api.get(`/share/order/${shareCode}`)
        this.order = res.order
        this.sharer = res.sharer || {}
        this.shareInfo = res.shareInfo || {}
      } catch (err) {
        console.error('加载分享订单失败:', err)
        this.error = err.response?.data?.message || err.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    formatPrice(price) {
      if (!price) return '0.00'
      return Number(price).toFixed(2)
    },
    formatDateTime(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return d.toLocaleDateString('zh-CN') + ' ' + d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    },
    getStatusText(status) {
      const statusMap = {
        'PENDING': '待确认',
        'CONFIRMED': '已确认',
        'PROCESSING': '处理中',
        'DELIVERING': '配送中',
        'DELIVERED': '已送达',
        'COMPLETED': '已完成',
        'CANCELLED': '已取消',
        'REJECTED': '已拒绝'
      }
      return statusMap[status] || status
    },
    getStatusClass(status) {
      const classMap = {
        'PENDING': 'status-pending',
        'CONFIRMED': 'status-confirmed',
        'PROCESSING': 'status-processing',
        'DELIVERING': 'status-delivering',
        'DELIVERED': 'status-delivered',
        'COMPLETED': 'status-completed',
        'CANCELLED': 'status-cancelled',
        'REJECTED': 'status-rejected'
      }
      return classMap[status] || ''
    },
    goLogin() {
      const shareCode = this.$route.params.shareCode
      this.$router.push({
        path: '/login',
        query: { redirect: `/orders`, shareCode }
      })
    }
  }
}
</script>

<style scoped>
.shared-order-page {
  max-width: 500px;
  margin: 0 auto;
  background: #f5f5f5;
  min-height: 100vh;
  padding-bottom: 80px;
}

.loading-state, .error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  color: #666;
}

.loading-spinner, .error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.order-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px 16px;
  text-align: center;
}

.order-status {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
}

.order-no {
  font-size: 13px;
  opacity: 0.9;
}

.order-section {
  background: white;
  margin-top: 8px;
  padding: 16px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.order-items {
  margin-bottom: 12px;
}

.order-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.order-item:last-child {
  border-bottom: none;
}

.item-image, .item-image-placeholder {
  width: 60px;
  height: 60px;
  border-radius: 6px;
  object-fit: cover;
  margin-right: 12px;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
}

.item-info {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-size: 14px;
  color: #333;
  font-weight: 500;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-spec {
  font-size: 12px;
  color: #888;
}

.item-price {
  font-size: 15px;
  font-weight: 600;
  color: #ff4d4f;
  margin-left: 12px;
}

.order-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #eee;
  font-size: 14px;
  color: #333;
}

.total-amount {
  font-size: 18px;
  font-weight: 700;
  color: #ff4d4f;
}

.item-tax {
  font-size: 11px;
  color: #ff9800;
  margin-top: 2px;
}

.order-summary {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 14px;
  color: #666;
}

.summary-row.total {
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid #eee;
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.summary-row.total .total-amount {
  font-size: 20px;
  color: #ff4d4f;
}

.info-row {
  display: flex;
  padding: 8px 0;
  font-size: 14px;
}

.info-label {
  color: #888;
  width: 80px;
  flex-shrink: 0;
}

.info-value {
  color: #333;
  flex: 1;
}

.bottom-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  box-shadow: 0 -2px 8px rgba(0,0,0,0.1);
  max-width: 500px;
  margin: 0 auto;
}

.btn-login {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}
</style>
